import { Chip } from "@mui/material";
import React, { useEffect, useState } from "react";
import { emailRegex, phoneRegex } from "../../config/config";
import { soulManager } from "../../data/managers/SoulManager";
import {
  CreditApplication,
  CreditStatus,
} from "../../data/models/applications/CreditApplication";
import { useAuth } from "../../data/providers/AuthProvider";
import { useUserData } from "../../data/providers/UserDataProvider";

type FormErrors = Partial<
  Record<
    | "applicantName"
    | "document"
    | "documentType"
    | "amount"
    | "termMonths"
    | "purpose"
    | "emailOptional"
    | "guarantorName"
    | "guarantorPhone"
    | "accountBank"
    | "accountType"
    | "accountNumber"
    | "general",
    string
  >
>;

const onlyDigits = (v?: string) => (v || "").replace(/\D/g, "");
const fmtMoney = (n: number) =>
  (isNaN(n) ? 0 : n).toLocaleString("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  });
export const nowIso = () => new Date().toISOString();

const StatusChip: React.FC<{ status: CreditStatus }> = ({ status }) => {
  const map: Record<
    CreditStatus,
    { label: string; color: "default" | "success" | "error" | "warning" }
  > = {
    PENDING: { label: "Pendiente", color: "warning" },
    APPROVED: { label: "Aprobada", color: "success" },
    REJECTED: { label: "Rechazada", color: "error" },
    FINISHED: { label: "Finalizada", color: "success" },
  };
  return (
    <Chip size="small" label={map[status].label} color={map[status].color} />
  );
};

const validateDocumentByType = (
  type: CreditApplication["documentType"],
  value: string
): string | undefined => {
  const digits = onlyDigits(value);
  if (type === "CC" || type === "TI") {
    if (digits.length < 6 || digits.length > 12) return "Documento inválido";
  } else if (type === "CE") {
    if (digits.length < 5 || digits.length > 12) return "Documento inválido";
  } else if (type === "NIT") {
    if (digits.length < 8 || digits.length > 15) return "NIT inválido";
  } else if (type === "PAS") {
    if (value.length < 5 || value.length > 15) return "Pasaporte inválido";
  }
  return undefined;
};

function validateForm(
  f: Partial<CreditApplication>,
  canEditIdentity: { name: boolean; doc: boolean; email: boolean }
): FormErrors {
  const errors: FormErrors = {};

  if (canEditIdentity.name) {
    if (!f.applicantName || f.applicantName.trim().length < 2) {
      errors.applicantName = "Nombre requerido (mín. 2 caracteres)";
    } else if (f.applicantName.length > 80) {
      errors.applicantName = "Máximo 80 caracteres";
    }
  }

  if (canEditIdentity.doc) {
    if (!f.documentType) {
      errors.documentType = "Selecciona un tipo";
    }
    if (!f.document) {
      errors.document = "Documento requerido";
    } else {
      const docErr = validateDocumentByType(
        (f.documentType as CreditApplication["documentType"]) || "CC",
        f.document
      );
      if (docErr) errors.document = docErr;
    }
  }

  if (
    f.amount == null ||
    isNaN(f.amount as number) ||
    (f.amount as number) <= 0
  ) {
    errors.amount = "Monto debe ser mayor a 0";
  }

  if (f.termMonths == null || isNaN(f.termMonths as number)) {
    errors.termMonths = "Plazo requerido";
  } else if ((f.termMonths as number) < 1 || (f.termMonths as number) > 84) {
    errors.termMonths = "Plazo entre 1 y 84 meses";
  }

  if (!f.purpose || f.purpose.trim().length < 3) {
    errors.purpose = "Propósito requerido (mín. 3 caracteres)";
  } else if (f.purpose.length > 120) {
    errors.purpose = "Máximo 120 caracteres";
  }

  if (
    canEditIdentity.email &&
    f.emailOptional &&
    !emailRegex.test(f.emailOptional)
  ) {
    errors.emailOptional = "Correo inválido";
  }

  if (!f.guarantorName || f.guarantorName.trim().length < 3) {
    errors.guarantorName = "Nombre del avalista requerido (mín. 3 caracteres)";
  }

  if (!f.guarantorPhone) {
    errors.guarantorPhone = "Teléfono del avalista requerido";
  } else if (!phoneRegex.test(f.guarantorPhone)) {
    errors.guarantorPhone = "Teléfono del avalista inválido";
  }
  if (!f.accountType) {
    errors.accountType = "Tipo de cuenta requerido";
  }

  if (!f.accountNumber) {
    errors.accountNumber = "Número de cuenta requerido";
  } else if (f.accountNumber.length < 6) {
    errors.accountNumber = "El número de cuenta debe tener al menos 6 dígitos";
  }

  if (!f.accountBank) {
    errors.accountBank = "Banco requerido";
  }

  return errors;
}

async function apiCreateOrUpdate({
  payload,
}: {
  payload: Partial<CreditApplication>;
}) {
  return soulManager.createSoulApplication(payload);
}

export function useApplications() {
  const userData = useUserData()!;
  const { user } = useAuth();
  const uid = user?.uid;

  const [items, setItems] = useState<CreditApplication[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [initValidation, setInitValidation] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CreditApplication | null>(null);
  const [form, setForm] = useState<Partial<CreditApplication>>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const isEdit = Boolean(editing);
  const [showVerifyDialog, setShowVerifyDialog] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info" | "warning";
  }>({ open: false, message: "", severity: "info" });

  const profileHasName = Boolean(
    userData?.data?.fullName && userData.data?.fullName.trim().length >= 2
  );
  const profileHasCreateApplications = Boolean(
    userData?.data?.document &&
      userData?.data?.documentType &&
      userData?.data?.isVerified
  );
  const profileHasEmail = Boolean(user?.email);
  const canEditIdentity = {
    name: !profileHasName,
    doc: !profileHasCreateApplications,
    email: !profileHasEmail,
  };

  const loadApplications = async () => {
    if (!uid) return;
    setLoading(true);
    try {
      const rows = await soulManager.getAllApplications();
      console.log(rows);
      rows?.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      setItems(rows ?? []);
    } finally {
      setLoading(false);
    }
  };

  const createOrUpdateApplication = async (
    payload: Partial<CreditApplication>
  ) => {
    if (!uid) return;

    const base: Partial<CreditApplication> = {
      ...payload,
      applicantName: profileHasName
        ? (userData.data?.fullName as string)
        : payload.applicantName,
      document: profileHasCreateApplications
        ? (userData.data?.document as string)
        : payload.document,
      documentType: profileHasCreateApplications
        ? (userData.data?.documentType as any)
        : payload.documentType,
      emailOptional: profileHasEmail
        ? user?.email
        : payload.emailOptional || null,
      guarantorName: payload.guarantorName,
      guarantorPhone: payload.guarantorPhone,
      accountBank: payload.accountBank,
      accountNumber: payload.accountNumber,
      accountType: payload.accountType,
      phoneOptional: user?.phoneNumber || null,
    };
    if (!payload.id) {
      base.status = "PENDING";
    }

    const out: Record<string, any> = {};
    Object.entries(base).forEach(([k, v]) => {
      if (v !== undefined) out[k] = v === "" ? null : v;
    });

    const result = await apiCreateOrUpdate({ payload: base });
    if (!result) throw new Error("Error en createSoulApplication");
    await loadApplications();
  };

  const uploadConsentApplication = async (
    payload: Partial<CreditApplication>
  ) => {
    if (!uid) return;

    const base: Partial<CreditApplication> = {
      ...payload,
    };

    const out: Record<string, any> = {};
    Object.entries(base).forEach(([k, v]) => {
      if (v !== undefined) out[k] = v === "" ? null : v;
    });

    const result = await soulManager.updateSoulApplication(payload);
    if (!result) throw new Error("Error en uploadConsentApplication");
    await loadApplications();
  };

  const createInstallment = async (
    applicationId: string,
    installmentId: string,
    url: string
  ) => {
    const result = await soulManager.createInstallmentApplication({
      applicationId,
      installmentId,
      url,
    });
    if (!result) throw new Error("Error en createInstallment");
    await loadApplications();
  };

  useEffect(() => {
    if (uid) loadApplications();
  }, [uid]);

  useEffect(() => {
    if (!initValidation) return;
    const v = validateForm(form, canEditIdentity);
    setErrors((prev) => {
      const prevStr = JSON.stringify(prev);
      const nextStr = JSON.stringify(v);
      return prevStr === nextStr ? prev : v;
    });
  }, [form, canEditIdentity, initValidation]);

  const openCreate = () => {
    if (!profileHasName || !profileHasCreateApplications) {
      setShowVerifyDialog(true);
      return;
    }
    setEditing(null);
    setForm({
      applicantName: profileHasName ? userData.data?.fullName : "",
      document: profileHasCreateApplications
        ? (userData.data?.document as string)
        : "",
      documentType: profileHasCreateApplications
        ? (userData.data?.documentType as any)
        : "CC",
      amount: 0,
      termMonths: 12,
      purpose: "",
      guarantorName: "",
      guarantorPhone: "",
      emailOptional: profileHasEmail ? user?.email || "" : "",
      phoneOptional: user?.phoneNumber || "",
    });
    setErrors({});
    setOpen(true);
  };

  const openEdit = (row: CreditApplication) => {
    setEditing(row);
    setForm({ ...row });
    setErrors({});
    setOpen(true);
  };

  const closeDialog = () => setOpen(false);

  const handleChange =
    (field: keyof CreditApplication) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value: any = e.target.value;

      if (field === "amount") {
        const clean = onlyDigits(value);
        value = Number(clean);
      } else if (field === "termMonths") {
        value = Number(value);
      }

      setForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

  const adjustTermMonths = (delta: number) => {
    setForm((prev) => {
      const current = prev.termMonths ?? 12;
      const newVal = Math.max(6, Math.min(84, current + delta));
      return { ...prev, termMonths: newVal };
    });
  };

  const saveForm = async () => {
    setInitValidation(true);
    const v = validateForm(form, canEditIdentity);
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    setIsSending(true);
    try {
      await createOrUpdateApplication({
        id: editing?.id,
        applicantName: form.applicantName!,
        document: form.document!,
        documentType: form.documentType as CreditApplication["documentType"],
        amount: Number(form.amount),
        termMonths: Number(form.termMonths),
        purpose: form.purpose!,
        guarantorName: form.guarantorName!,
        guarantorPhone: form.guarantorPhone!,
        emailOptional: (form.emailOptional || null) as string | null,
        accountBank: form.accountBank,
        accountNumber: form.accountNumber,
        accountType: form.accountType,
      });

      setSnackbar({
        open: true,
        message: "Solicitud enviada correctamente ✅",
        severity: "success",
      });
      setOpen(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo enviar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const uploadConsent = async (id: string, url: string) => {
    try {
      await uploadConsentApplication({
        id: id,
        isConsentAvailable: false,
        consentPdfUploaded: url,
      });
      setSnackbar({
        open: true,
        message: "Solicitud actualizada correctamente ✅",
        severity: "success",
      });
      setOpen(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo actualizar la solicitud. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  const uploadInstallment = async (
    applicationId: string,
    installmentId: string,
    url: string
  ) => {
    try {
      await createInstallment(applicationId, installmentId, url);
      setSnackbar({
        open: true,
        message: "Pago enviado correctamente ✅",
        severity: "success",
      });
      setOpen(false);
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: "No se pudo enviar el pago. Intenta de nuevo.",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return {
    userData,
    loadApplications,
    loading,
    openCreate,
    user,
    items,
    fmtMoney,
    StatusChip,
    openEdit,
    isEdit,
    canEditIdentity,
    form,
    handleChange,
    adjustTermMonths,
    errors,
    closeDialog,
    saveForm,
    open,
    setShowVerifyDialog,
    showVerifyDialog,
    setForm,
    setErrors,
    isSending,
    snackbar,
    setSnackbar,
    uploadConsent,
    uploadInstallment,
  };
}
